import { UniqueIdentifier } from "@dnd-kit/core";
import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { WarrantySystemView } from "@redotech/redo-return-operations-common/views/default-return-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  OrderManagementSuiteStatusesContext,
  WARRANTIES_ICON,
  WARRANTIES_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { WarrantyViewOrderingContext } from "../../../../services/warranties/warranty-view-ordering-service";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const WarrantiesItem = memo(function WarrantiesItem() {
  const { t, i18n } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { warrantiesEnabled, warrantyRegistrationEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const hasPermissionToViewItem = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
  );

  const warrantyViewOrderingContext = useRequiredContext(
    WarrantyViewOrderingContext,
  );

  const allViews = useMemo(() => {
    return [...warrantyViewOrderingContext.views].filter(
      (view) => view.name !== WarrantySystemView.CREATE,
    );
  }, [warrantyViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...warrantyViewOrderingContext.folders];
  }, [warrantyViewOrderingContext]);

  const notificationCounts = warrantyViewOrderingContext.notificationCounts;

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"warranties">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const sortableItems = useMemo(() => {
    void i18n.language;
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix: routes.warrantiesHref,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    routes.warrantiesHref,
    notificationCounts,
    folderNotificationCounts,
    i18n.language,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    warrantyViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"warranties">(
      flattenedItems,
      allViews,
      allFolders,
    );
    warrantyViewOrderingContext.updateOrdering(views, folders);
  });

  const registrationLink = routes.registrationsHref;

  const warrantiesMatch = useMatch(
    `${routes.getWarrantiesHref({ enabled: true })}/*`,
  );
  const registrationsMatch = useMatch(`${registrationLink}/*`);
  const anyChildSelected = warrantiesMatch || registrationsMatch;

  if (!hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getWarrantiesHref({ enabled: warrantiesEnabled }),
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={WARRANTIES_ICON_FILLED}
      friendlyName={t("Warranties")}
      id="warranties"
      notificationCount={totalNotificationCount}
      UnfilledIcon={WARRANTIES_ICON}
    >
      <AllWarrantiesItem hidden={!warrantiesEnabled} />
      <WarrantyRegistrationsItem
        hidden={!warrantyRegistrationEnabled}
        href={registrationLink}
      />
      {warrantiesEnabled && (
        <SortableTree
          items={sortableItems}
          onRequestEdit={handleRequestEdit}
          setItems={handleUpdateOrdering}
        />
      )}
      <WarrantiesAddViewItem hidden={!warrantiesEnabled} />
      <WarrantiesCreateFolderItem hidden={!warrantiesEnabled} />
    </RedoSecondaryNavItem>
  );
});

const AllWarrantiesItem = memo(function AllWarrantiesItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getWarrantiesSubRoute("all-warranties"),
      }}
      friendlyName={t("All warranties")}
      hidden={hidden}
      id="all-warranties"
    />
  );
});

const WarrantyRegistrationsItem = memo(function WarrantyRegistrationsItem({
  href,
  hidden,
}: {
  href: string;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={t("Registrations")}
      hidden={hidden}
      id="registrations"
    />
  );
});

const WarrantiesAddViewItem = memo(function WarrantiesAddViewItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getWarrantiesSubRoute("create") }}
      friendlyName={t("+ Add new view")}
      hidden={hidden}
      id="orders-warranties-create"
    />
  );
});

const WarrantiesCreateFolderItem = memo(function WarrantiesCreateFolderItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const warrantyViewOrderingContext = useRequiredContext(
    WarrantyViewOrderingContext,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          warrantyViewOrderingContext.openCreateFolderModal();
        },
      }}
      friendlyName={t("+ Add new folder")}
      hidden={hidden}
      id="orders-warranties-create-folder"
    />
  );
});
